"use client";

import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { useLogin } from "@/api/auth.api";
import { useAuth } from "@/context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import AuthLayout from "@/components/AuthLayout";

function FloatingInput({
  id,
  label,
  type = "text",
  value,
  onChange,
  suffix,
}: {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  suffix?: React.ReactNode;
}) {
  const [focused, setFocused] = useState(false);
  const [autofilled, setAutofilled] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    const handler = (e: AnimationEvent) => {
      if (e.animationName === "onAutoFillStart") setAutofilled(true);
      if (e.animationName === "onAutoFillCancel") setAutofilled(false);
    };
    el.addEventListener("animationstart", handler);
    return () => el.removeEventListener("animationstart", handler);
  }, []);

  const lifted = focused || value.length > 0 || autofilled;

  return (
    <div className="relative">
      <style>{`
        @keyframes onAutoFillStart { from {} to {} }
        @keyframes onAutoFillCancel { from {} to {} }
        input:-webkit-autofill { animation-name: onAutoFillStart; animation-duration: 0.001s; }
        input:not(:-webkit-autofill) { animation-name: onAutoFillCancel; animation-duration: 0.001s; }
      `}</style>
      <input
        ref={inputRef}
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder=""
        autoComplete={type === "password" ? "current-password" : id}
        style={{
          paddingRight: suffix ? "3rem" : undefined,
          background: "#dde3f5",
          border: "1px solid #a0aace",
        }}
        className="w-full rounded-xl px-4 pt-5 pb-1.5 text-gray-800 outline-none focus:ring-2 focus:ring-indigo-300 transition text-sm"
      />
      <label
        htmlFor={id}
        className="absolute left-4 pointer-events-none select-none transition-all duration-150"
        style={{
          top: lifted ? "6px" : "50%",
          transform: lifted ? "translateY(0)" : "translateY(-50%)",
          fontSize: lifted ? "10px" : "13px",
          color: focused ? "#6366f1" : "#6b7280",
        }}
      >
        {label}
      </label>
      {suffix && (
        <div className="absolute right-4 top-1/2 -translate-y-1/2">
          {suffix}
        </div>
      )}
    </div>
  );
}

export default function SignInPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const router = useRouter();
  const queryClient = useQueryClient();
  const { mutateAsync: login, isPending: isLoading } = useLogin();
  const { setUserId } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const response: any = await login({ username, password });
      const { userId, token, userType } = response.data;

      const apiHost = new URL(process.env.NEXT_PUBLIC_API_BASE_URL!).hostname;
      const currentHost = window.location.hostname;
      if (apiHost !== currentHost && token) {
        localStorage.setItem("jwt_token", token);
      }

      queryClient.clear();
      await queryClient.invalidateQueries({ queryKey: ["me"] });

      sessionStorage.setItem("welcome_user", "1");
      router.push(`/dashboard/${userType}/${userId}`);
    } catch {
      setError("Invalid credentials, please try again.");
    }
  };

  const eyeIcon = (
    <button
      type="button"
      onClick={() => setShowPassword(!showPassword)}
      className="text-gray-400 hover:text-indigo-500 transition"
    >
      {showPassword ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M13.875 18.825A10.05 10.05 0 0112 19c-5 0-9-7-9-7a17.226 17.226 0 013.362-4.362M6.61 6.61A9.956 9.956 0 0112 5c5 0 9 7 9 7a16.88 16.88 0 01-2.163 2.994M15 12a3 3 0 00-3-3m0 0a3 3 0 00-2.83 4M3 3l18 18"
          />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-5 h-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
      )}
    </button>
  );

  return (
    <AuthLayout>
      <form onSubmit={handleSubmit}>
        <div className="px-8 py-8">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
            Sign In
          </h2>

          {error && (
            <div className="mb-4 px-4 py-2 rounded-lg text-sm bg-red-50 border border-red-200 text-red-600">
              {error}
            </div>
          )}

          <div className="flex flex-col gap-3 mb-5">
            <FloatingInput
              id="username"
              label="Username"
              value={username}
              onChange={setUsername}
            />
            <FloatingInput
              id="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={setPassword}
              suffix={eyeIcon}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 rounded-xl text-white font-semibold text-base transition-opacity hover:opacity-90 active:opacity-80"
            style={{
              background: "linear-gradient(90deg, #667eea, #764ba2)",
              boxShadow: "0 4px 15px rgba(106,111,212,0.4)",
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            {isLoading ? "Signing in..." : "Sign In"}
          </button>

          <p className="text-center mt-4 text-sm">
            <a
              href="/forgot-password"
              className="text-indigo-500 hover:text-indigo-700 hover:underline transition"
            >
              Forgot Password?
            </a>
          </p>
        </div>
      </form>
    </AuthLayout>
  );
}
